import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Pagination,
  Select,
  Skeleton,
  Typography,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import UndoIcon from "@mui/icons-material/Undo";

import Panel from "../../components/brand/Panel";
import {
  getBankStatementLine,
  getBankStatementLines,
  ignoreStatementLine,
  reconcileOwnerPayment,
  reconcileSupplierPayment,
  unignoreStatementLine,
} from "../../api/financeApi";
import { getApiErrorMessage, isConcurrencyConflict } from "../../utils/concurrency";
import { formatDateUk } from "../../utils/dateDisplay";

const PAGE_SIZE = 10;
const CONFIDENT_SCORE = 50;

const money = (value) =>
  value == null
    ? "—"
    : new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(value);

export default function BankReconciliationPanel({ propertyId, onMessage, onError }) {
  const [statusFilter, setStatusFilter] = useState("unreconciled");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [queueTotal, setQueueTotal] = useState(0);
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState(null);
  const [menu, setMenu] = useState(null);
  const [ignoreTarget, setIgnoreTarget] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      onError?.("");
      const params = { page_size: PAGE_SIZE };
      if (statusFilter) params.status = statusFilter;
      const [listRes, queueRes] = await Promise.all([
        getBankStatementLines(propertyId, page, params),
        statusFilter === "unreconciled"
          ? Promise.resolve(null)
          : getBankStatementLines(propertyId, 1, { page_size: 1, status: "unreconciled" }),
      ]);
      const results = listRes.data.results || [];
      setTotalPages(listRes.data.total_pages || 1);
      setQueueTotal(
        statusFilter === "unreconciled" ? listRes.data.total || 0 : queueRes?.data?.total || 0
      );
      const details = await Promise.all(
        results.map((line) =>
          getBankStatementLine(propertyId, line.id)
            .then((res) => res.data)
            .catch(() => ({ ...line, suggestions: [], suggestions_error: true }))
        )
      );
      setCards(details);
    } catch (e) {
      onError?.(getApiErrorMessage(e, "Failed to load statement lines."));
    } finally {
      setLoading(false);
    }
  }, [propertyId, page, statusFilter, onError]);

  useEffect(() => {
    load();
  }, [load]);

  const topSuggestion = (card) =>
    [...(card.suggestions || [])].sort((a, b) => (b.score || 0) - (a.score || 0))[0] || null;

  const confirmSuggestion = async (card, suggestion) => {
    try {
      setActingId(card.id);
      onError?.("");
      if (suggestion.type === "owner_invoice") {
        await reconcileOwnerPayment(propertyId, card.id, {
          invoice_id: suggestion.invoice_id,
          row_version: card.row_version,
        });
        onMessage?.(`Matched to owner invoice ${suggestion.invoice_number}.`);
      } else {
        await reconcileSupplierPayment(propertyId, card.id, {
          supplier_invoice_id: suggestion.invoice_id,
          row_version: card.row_version,
        });
        onMessage?.(`Matched to supplier invoice ${suggestion.invoice_number}.`);
      }
      await load();
    } catch (e) {
      if (isConcurrencyConflict(e)) {
        onError?.("This statement line was changed elsewhere. Reloading…");
        await load();
      } else {
        onError?.(getApiErrorMessage(e, "Failed to confirm the match."));
      }
    } finally {
      setActingId(null);
    }
  };

  const handleIgnore = async () => {
    if (!ignoreTarget) return;
    try {
      setActingId(ignoreTarget.id);
      onError?.("");
      await ignoreStatementLine(propertyId, ignoreTarget.id, ignoreTarget.row_version);
      onMessage?.("Statement line ignored.");
      setIgnoreTarget(null);
      await load();
    } catch (e) {
      if (isConcurrencyConflict(e)) {
        onError?.("This statement line was changed elsewhere. Reloading…");
        setIgnoreTarget(null);
        await load();
      } else {
        onError?.(getApiErrorMessage(e, "Failed to ignore statement line."));
      }
    } finally {
      setActingId(null);
    }
  };

  const handleUnignore = async (card) => {
    try {
      setActingId(card.id);
      onError?.("");
      await unignoreStatementLine(propertyId, card.id, card.row_version);
      onMessage?.("Statement line restored to the queue.");
      await load();
    } catch (e) {
      if (isConcurrencyConflict(e)) {
        onError?.("This statement line was changed elsewhere. Reloading…");
        await load();
      } else {
        onError?.(getApiErrorMessage(e, "Failed to unignore statement line."));
      }
    } finally {
      setActingId(null);
    }
  };

  return (
    <>
      <Panel
        eyebrow={`${queueTotal} item${queueTotal === 1 ? "" : "s"} to reconcile`}
        title="Reconciliation"
        noBodyPadding={false}
        right={
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Show</InputLabel>
            <Select
              label="Show"
              value={statusFilter}
              onChange={(e) => {
                setPage(1);
                setStatusFilter(e.target.value);
              }}
            >
              <MenuItem value="unreconciled">To reconcile</MenuItem>
              <MenuItem value="reconciled">Reconciled</MenuItem>
              <MenuItem value="ignored">Ignored</MenuItem>
              <MenuItem value="">All</MenuItem>
            </Select>
          </FormControl>
        }
      >
        {loading ? (
          <Skeleton height={280} />
        ) : cards.length === 0 ? (
          <Alert severity="success">
            {statusFilter === "unreconciled"
              ? "Nothing left to reconcile. Import a statement on the Bank tab to add lines."
              : "No statement lines in this view."}
          </Alert>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {cards.map((card) => {
              const savedMatch = card.status === "reconciled" ? card.match : null;
              const suggestion = topSuggestion(card);
              const confident = suggestion && (suggestion.score || 0) >= CONFIDENT_SCORE;
              const shownSuggestion = !savedMatch && confident ? suggestion : null;
              const canMatch = ["unreconciled", "suggested"].includes(card.status);
              const busy = actingId === card.id;
              return (
                <Box
                  key={card.id}
                  sx={(theme) => ({
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "1fr auto 1fr auto" },
                    gap: 2,
                    alignItems: "center",
                    p: 2,
                    border: `1px solid ${theme.palette.brand.border}`,
                    borderRadius: 1.5,
                    backgroundColor:
                      confident && canMatch
                        ? theme.palette.mode === "dark"
                          ? "rgba(95, 160, 87, 0.12)"
                          : "rgba(63, 122, 58, 0.08)"
                        : "transparent",
                  })}
                >
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {formatDateUk(card.transaction_date)} ·{" "}
                      {card.direction === "in" ? "Money in" : "Money out"}
                    </Typography>
                    <Typography variant="h6" sx={{ my: 0.25 }}>
                      {money(card.amount)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {card.description || card.counterparty_name || "—"}
                      {card.reference ? ` · ${card.reference}` : ""}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                    {canMatch && shownSuggestion ? (
                      <Button
                        variant="contained"
                        color="success"
                        startIcon={
                          busy ? <CircularProgress size={16} color="inherit" /> : <CheckIcon />
                        }
                        disabled={busy}
                        onClick={() => confirmSuggestion(card, shownSuggestion)}
                        sx={{ minWidth: 88, minHeight: 44 }}
                      >
                        OK
                      </Button>
                    ) : (
                      <Chip size="small" label={card.status} />
                    )}
                  </Box>

                  <Box>
                    {savedMatch?.invoice_number ? (
                      <>
                        <Typography variant="body2" fontWeight={600}>
                          {savedMatch.invoice_number}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {savedMatch.type === "owner_invoice"
                            ? `${savedMatch.unit_name || "Unit"} · ${savedMatch.owner_name || "Owner"}`
                            : savedMatch.supplier_name || "Supplier"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Matched {money(savedMatch.amount)} · Outstanding{" "}
                          {money(savedMatch.outstanding)}
                        </Typography>
                      </>
                    ) : card.suggestions_error ? (
                      <Typography variant="body2" color="error">
                        Could not look up matching invoices.
                      </Typography>
                    ) : shownSuggestion ? (
                      <>
                        <Typography variant="body2" fontWeight={600}>
                          {shownSuggestion.invoice_number}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {shownSuggestion.type === "owner_invoice"
                            ? `${shownSuggestion.unit_name || "Unit"} · ${shownSuggestion.owner_name || "Owner"}`
                            : shownSuggestion.supplier_name || "Supplier"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Outstanding {money(shownSuggestion.outstanding)}
                          {(shownSuggestion.reasons || []).length
                            ? ` · ${(shownSuggestion.reasons || []).join(", ")}`
                            : ""}
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {card.status === "reconciled"
                          ? "Reconciled. The matched invoice is not on this line."
                          : card.direction === "in"
                            ? "No matching posted owner invoice."
                            : "No matching approved supplier invoice."}
                      </Typography>
                    )}
                  </Box>

                  <IconButton
                    size="small"
                    aria-label="More actions"
                    disabled={busy}
                    onClick={(event) => setMenu({ anchor: event.currentTarget, card })}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </Box>
              );
            })}
          </Box>
        )}

        {totalPages > 1 && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <Pagination count={totalPages} page={page} onChange={(_, value) => setPage(value)} />
          </Box>
        )}
      </Panel>

      <Menu
        anchorEl={menu?.anchor}
        open={Boolean(menu)}
        onClose={() => setMenu(null)}
      >
        {menu && ["unreconciled", "suggested"].includes(menu.card.status) && (
          <MenuItem
            onClick={() => {
              setIgnoreTarget(menu.card);
              setMenu(null);
            }}
          >
            <VisibilityOffIcon fontSize="small" sx={{ mr: 1 }} />
            Ignore
          </MenuItem>
        )}
        {menu?.card.status === "ignored" && (
          <MenuItem
            onClick={() => {
              const card = menu.card;
              setMenu(null);
              handleUnignore(card);
            }}
          >
            <UndoIcon fontSize="small" sx={{ mr: 1 }} />
            Unignore
          </MenuItem>
        )}
        {menu && !["unreconciled", "suggested", "ignored"].includes(menu.card.status) && (
          <MenuItem disabled>No further actions</MenuItem>
        )}
      </Menu>

      <Dialog open={Boolean(ignoreTarget)} onClose={() => !actingId && setIgnoreTarget(null)}>
        <DialogTitle>Ignore statement line?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Ignore {ignoreTarget ? money(ignoreTarget.amount) : ""} on{" "}
            {ignoreTarget ? formatDateUk(ignoreTarget.transaction_date) : ""}? Restore it later
            from the Ignored view.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIgnoreTarget(null)} disabled={Boolean(actingId)}>
            Cancel
          </Button>
          <Button
            variant="outlined"
            color="warning"
            startIcon={
              actingId ? <CircularProgress size={16} color="inherit" /> : <VisibilityOffIcon />
            }
            onClick={handleIgnore}
            disabled={Boolean(actingId)}
          >
            Ignore
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
